import { Component, EventEmitter, Input, OnDestroy, Output, ElementRef, ViewChild, ChangeDetectionStrategy } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { MenuItem } from 'primeng/api';

import { DrawerModule } from 'primeng/drawer';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { DialogModule } from 'primeng/dialog';
import { DividerModule } from 'primeng/divider';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { MenuModule } from 'primeng/menu';
import { ToggleSwitchModule } from 'primeng/toggleswitch';

import { ConfigService } from '../../_services/config.service';
import { CompaniesService } from '../../companies/services/companies.service';
import { MessagingService } from '../_services/messaging.service';
import { ChatBrowserNotificationService } from '../_services/chat-browser-notification.service';
import { CmpConversation, CmpMessage, ChatUser, CmpAttachment, CmpConversationMember, ChatSettings } from '../_models/messaging.model';

/**
 * Pannello chat come drawer laterale (da destra) con split-view interno.
 * Versione non bloccante: tutte le chiamate usano X-No-Spinner (silent),
 * caricamento e refresh avvengono in background senza spinner globale.
 */
@Component({
	standalone: true,
	selector: 'app-chat-panel',
	templateUrl: './chat-panel.component.html',
	styleUrls: ['../lista/conversations-list.component.scss', './chat-panel.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [FormsModule, TranslatePipe, DrawerModule, ButtonModule, TooltipModule, DialogModule, DividerModule, SelectModule, MultiSelectModule, MenuModule, ToggleSwitchModule],
})
export class ChatPanelComponent implements OnDestroy {
	@Input() set visible(v: boolean) {
		if (v === this._visible) return;
		this._visible = v;
		if (v) this.onOpen();
		else this.onClose();
	}
	get visible(): boolean {
		return this._visible;
	}
	@Output() visibleChange = new EventEmitter<boolean>();
	/** Emesso quando il conteggio non letti può essere cambiato (per aggiornare il badge in header). */
	@Output() unreadChanged = new EventEmitter<void>();

	@ViewChild('messagesContainer') messagesContainer?: ElementRef;
	@ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;

	private _visible = false;

	// Azioni messaggio (menu per-messaggio)
	msgMenuItems: MenuItem[] = [];
	private attUrlCache = new Map<number, string>();
	private attPending = new Set<number>();

	// Modifica messaggio
	showEditDialog: boolean = false;
	editingMsg: CmpMessage | null = null;
	editingText: string = '';

	// Creazione gruppo (riusa il dialog "nuova conversazione")
	groupMode: boolean = false;
	groupName: string = '';
	groupSelectedUsers: ChatUser[] = [];

	// Gestione gruppo
	showGroupDialog: boolean = false;
	groupMembers: CmpConversationMember[] = [];
	isGroupAdmin: boolean = false;
	groupAddUsers: ChatUser[] = [];
	groupAddSelected: ChatUser | null = null;

	cmpId: number = 0;
	currentUserId: number = 0;

	conversations: CmpConversation[] = [];
	loading: boolean = false;
	searchTerm: string = '';

	selectedConv: CmpConversation | null = null;
	messages: CmpMessage[] = [];
	loadingMessages: boolean = false;
	newMessageText: string = '';
	sending: boolean = false;

	convMenuItems: MenuItem[] = [];
	sidebarOpen: boolean = false;

	showNewConvDialog: boolean = false;
	users: ChatUser[] = [];
	selectedUser: ChatUser | null = null;
	searchingUsers: boolean = false;

	// Impostazioni chat
	showSettingsDialog: boolean = false;
	settings: ChatSettings = { email_notify: false };
	browserNotify: boolean = true;

	// Ricerca stile WhatsApp: utenti con cui non c'e' ancora una chat
	searchNewUsers: ChatUser[] = [];
	private searchUsersTimer: any;

	// Modalita' chat dell'azienda attiva (vedi ChatContext)
	chatMode: 'DIRECT' | 'COMPANY' | 'DISABLED' = 'DIRECT';
	isCompanyMember: boolean = true;

	private refreshInterval: any;

	constructor(
		private router: Router,
		private configService: ConfigService,
		private companiesService: CompaniesService,
		private messagingService: MessagingService,
		private browserNotifications: ChatBrowserNotificationService,
	) {
		this.cmpId = this.configService.getCompany_id();
		this.currentUserId = this.configService.getUserID();
		this.convMenuItems = [
			{ label: this.configService.getTranslate('Aggiorna'), icon: 'pi pi-refresh', command: () => this.loadMessages() },
			{ label: this.configService.getTranslate('Segna come letto'), icon: 'pi pi-check', command: () => this.markAsRead() },
		];
	}

	ngOnDestroy(): void {
		this.clearRefresh();
		clearTimeout(this.searchUsersTimer);
		this.attUrlCache.forEach((url) => URL.revokeObjectURL(url));
		this.attUrlCache.clear();
	}

	// --- Apertura / chiusura drawer ---

	private onOpen(): void {
		// L'azienda attiva puo' essere cambiata mentre il drawer era chiuso (interval fermo):
		// invalida la selezione della vecchia azienda prima di ricaricare la lista.
		const current = this.configService.getCompany_id();
		if (current !== this.cmpId) {
			this.cmpId = current;
			this.selectedConv = null;
			this.messages = [];
		}
		this.loadChatContext();
		this.loadConversations();
		this.clearRefresh();
		this.refreshInterval = setInterval(() => this.refreshActive(), 15000);
	}

	/** In COMPANY il lato azienda non e' una persona: non c'e' nessun utente da cercare. */
	get canSearchUsers(): boolean {
		return this.chatMode !== 'COMPANY';
	}

	/** Cliente di un'azienda in COMPANY: l'unica controparte possibile e' l'azienda stessa. */
	get canWriteToCompany(): boolean {
		return this.chatMode === 'COMPANY' && !this.isCompanyMember;
	}

	/** Etichetta del pulsante che avvia la chat (dipende dalla modalità dell'azienda). */
	get newConvLabel(): string {
		return this.canWriteToCompany ? "Scrivi all'azienda" : 'Nuova Conversazione';
	}

	private loadChatContext(): void {
		if (!this.cmpId) return;
		this.companiesService.getChatContext(this.cmpId, true).subscribe({
			next: (ctx) => {
				this.chatMode = ctx.mode;
				this.isCompanyMember = ctx.isMember;
			},
			error: () => {},
		});
	}

	private onClose(): void {
		this.clearRefresh();
		this.unreadChanged.emit();
	}

	private clearRefresh(): void {
		if (this.refreshInterval) {
			clearInterval(this.refreshInterval);
			this.refreshInterval = null;
		}
	}

	close(): void {
		this.visible = false;
		this.visibleChange.emit(false);
	}

	onDrawerVisibleChange(v: boolean): void {
		this.visible = v;
		this.visibleChange.emit(v);
	}

	openFullPage(): void {
		this.close();
		this.router.navigate(['/messaging/lista']);
	}

	// --- Conversazioni ---

	loadConversations(): void {
		this.loading = true;
		this.messagingService.getConversations(true).subscribe({
			next: (data) => {
				this.conversations = data || [];
				if (!this.selectedConv && this.conversations.length > 0) {
					this.selectConversation(this.conversations[0]);
				}
			},
			error: () => {
				this.loading = false;
				this.configService.openErrorNotify('Errore caricamento conversazioni');
			},
			complete: () => {
				this.loading = false;
			},
		});
	}

	get filteredConversations(): CmpConversation[] {
		const t = this.searchTerm.trim().toLowerCase();
		if (!t) return this.conversations;
		return this.conversations.filter((c) => this.getOtherName(c).toLowerCase().includes(t) || (c.last_message_text || '').toLowerCase().includes(t));
	}

	/** Ricerca stile WhatsApp: sotto i risultati mostra anche le persone senza chat avviata. */
	onSearchTermChange(): void {
		clearTimeout(this.searchUsersTimer);
		const t = this.searchTerm.trim();
		if (!t || !this.canSearchUsers) {
			this.searchNewUsers = [];
			return;
		}
		this.searchUsersTimer = setTimeout(() => {
			this.messagingService.searchUsers(t, true).subscribe({
				next: (data) => {
					// Scarta le risposte obsolete: il debounce annulla solo il timer,
					// non l'HTTP già partito, quindi una risposta lenta di un termine
					// precedente potrebbe sovrascrivere i risultati di quello corrente.
					if (t !== this.searchTerm.trim()) return;
					// Escludi chi ha gia' una conversazione DIRECT nella lista
					const existing = new Set<number>();
					for (const c of this.conversations) {
						if (c.conv_type && c.conv_type !== 'DIRECT') continue;
						existing.add(c.conv_user1_id === this.currentUserId ? c.conv_user2_id : c.conv_user1_id);
					}
					this.searchNewUsers = (data || []).filter((u) => !existing.has(u.usr_id));
				},
			});
		}, 300);
	}

	startChatWith(user: ChatUser): void {
		this.messagingService.findOrCreateConversation(user.usr_id, true).subscribe({
			next: (conv) => {
				this.searchTerm = '';
				this.searchNewUsers = [];
				this.messagingService.getConversations(true).subscribe((data) => {
					this.conversations = data || [];
					const found = this.conversations.find((c) => c.conv_id === conv.conv_id) || conv;
					this.selectConversation(found);
				});
			},
			error: () => this.configService.openErrorNotify('Errore creazione conversazione'),
		});
	}

	userLabel(user: ChatUser): string {
		return [user.usr_lastname, user.usr_firstname].filter(Boolean).join(' ') || user.usr_username;
	}

	userInitials(user: ChatUser): string {
		const f = (user.usr_firstname || '').charAt(0).toUpperCase();
		const l = (user.usr_lastname || '').charAt(0).toUpperCase();
		return l + f || '?';
	}

	// --- Impostazioni chat ---

	openSettingsDialog(): void {
		this.browserNotify = this.browserNotifications.isEnabled();
		// Dialog aperto solo a caricamento riuscito: altrimenti "Salva" sovrascriverebbe
		// le preferenze sul server con i default locali
		this.messagingService.getChatSettings(true).subscribe({
			next: (s) => {
				this.settings = s;
				this.showSettingsDialog = true;
			},
			error: () => this.configService.openErrorNotify('Errore caricamento impostazioni'),
		});
	}

	saveSettings(): void {
		this.browserNotifications.setEnabled(this.browserNotify);
		this.messagingService.saveChatSettings(this.settings, true).subscribe({
			next: () => {
				this.showSettingsDialog = false;
			},
			error: () => this.configService.openErrorNotify('Errore salvataggio impostazioni'),
		});
	}

	selectConversation(conv: CmpConversation): void {
		this.selectedConv = conv;
		this.sidebarOpen = false;
		this.messages = [];
		this.buildConvMenu(conv);
		this.loadMessages();
	}

	isGroup(conv: CmpConversation | null): boolean {
		return !!conv && conv.conv_type === 'GROUP';
	}

	private buildConvMenu(conv: CmpConversation): void {
		const items: MenuItem[] = [
			{ label: this.configService.getTranslate('Aggiorna'), icon: 'pi pi-refresh', command: () => this.loadMessages() },
			{ label: this.configService.getTranslate('Segna come letto'), icon: 'pi pi-check', command: () => this.markAsRead() },
			{
				label: this.configService.getTranslate(conv.muted ? 'Riattiva notifiche' : 'Silenzia notifiche'),
				icon: conv.muted ? 'pi pi-bell' : 'pi pi-bell-slash',
				command: () => this.toggleMute(conv),
			},
		];
		if (this.isGroup(conv)) {
			items.push({ label: this.configService.getTranslate('Info gruppo'), icon: 'pi pi-users', command: () => this.openGroupDialog() });
			items.push({ label: this.configService.getTranslate('Abbandona gruppo'), icon: 'pi pi-sign-out', command: () => this.leaveGroup() });
		} else {
			items.push({ label: this.configService.getTranslate('Elimina conversazione'), icon: 'pi pi-trash', command: () => this.deleteConversation() });
		}
		this.convMenuItems = items;
	}

	toggleMute(conv: CmpConversation): void {
		const muted = !conv.muted;
		this.messagingService.setConversationMuted(conv.conv_id, muted, true).subscribe({
			next: () => {
				conv.muted = muted;
				this.buildConvMenu(conv);
			},
			error: () => this.configService.openErrorNotify('Errore'),
		});
	}

	deleteConversation(): void {
		if (!this.selectedConv) return;
		const convId = this.selectedConv.conv_id;
		this.messagingService.deleteConversation(convId, true).subscribe({
			next: () => {
				this.conversations = this.conversations.filter((c) => c.conv_id !== convId);
				this.selectedConv = null;
				this.messages = [];
			},
			error: () => this.configService.openErrorNotify('Errore eliminazione conversazione'),
		});
	}

	isSelected(conv: CmpConversation): boolean {
		return this.selectedConv?.conv_id === conv.conv_id;
	}

	// --- Messaggi ---

	loadMessages(): void {
		if (!this.selectedConv) return;
		const convId = this.selectedConv.conv_id;
		this.loadingMessages = true;
		this.messagingService.getMessages(convId, true).subscribe({
			next: (data) => {
				// Risposta tardiva di una conversazione non piu' selezionata: scartala
				if (this.selectedConv?.conv_id !== convId) return;
				const wasEmpty = this.messages.length === 0;
				const changed = !data || data.length !== this.messages.length;
				this.messages = data || [];
				this.hydrateAttachments();
				if (wasEmpty || changed) {
					this.markAsRead(convId);
					setTimeout(() => this.scrollToBottom(), 80);
				}
			},
			error: () => {
				this.loadingMessages = false;
			},
			complete: () => {
				this.loadingMessages = false;
			},
		});
	}

	refreshActive(): void {
		// Se l'azienda attiva e' cambiata mentre il pannello e' aperto, ricarica la lista scope-corretta
		const current = this.configService.getCompany_id();
		if (current !== this.cmpId) {
			this.cmpId = current;
			this.selectedConv = null;
			this.messages = [];
			this.loadConversations();
			return;
		}
		if (this.selectedConv) {
			this.loadMessages();
		}
	}

	markAsRead(convId?: number): void {
		convId = convId ?? this.selectedConv?.conv_id;
		if (!convId) return;
		this.messagingService.markAsRead(convId, true).subscribe(() => {
			const c = this.conversations.find((x) => x.conv_id === convId);
			if (c) c.unread_count = 0;
			this.unreadChanged.emit();
		});
	}

	sendMessage(): void {
		if (!this.selectedConv || !this.newMessageText.trim() || this.sending) return;
		const convId = this.selectedConv.conv_id;
		const text = this.newMessageText.trim();
		this.sending = true;
		this.newMessageText = '';

		this.messagingService.sendMessage(convId, text, true).subscribe({
			next: () => {
				this.loadMessages();
				const c = this.conversations.find((x) => x.conv_id === convId);
				if (c) {
					c.last_message_text = text;
					c.conv_last_message_at = new Date().toISOString();
				}
			},
			error: () => {
				this.sending = false;
				this.configService.openErrorNotify('Errore invio messaggio');
				// Ripristina la bozza solo se l'utente e' ancora sulla conversazione di destinazione
				if (this.selectedConv?.conv_id === convId) {
					this.newMessageText = text;
				}
			},
			complete: () => {
				this.sending = false;
			},
		});
	}

	onKeydown(event: KeyboardEvent): void {
		if (event.key === 'Enter' && !event.shiftKey) {
			event.preventDefault();
			this.sendMessage();
		}
	}

	// --- Nuova conversazione ---

	openNewConvDialog(): void {
		this.showNewConvDialog = true;
		this.selectedUser = null;
		this.groupMode = false;
		this.groupName = '';
		this.groupSelectedUsers = [];
		this.searchUsers('');
	}

	toggleGroupMode(): void {
		this.groupMode = !this.groupMode;
		this.selectedUser = null;
		this.groupSelectedUsers = [];
		this.searchUsers('');
	}

	searchUsers(query: string): void {
		// Chat 1:1 in modalità COMPANY: nessun risultato per costruzione, evita la chiamata
		if (!this.groupMode && !this.canSearchUsers) {
			this.users = [];
			return;
		}
		this.searchingUsers = true;
		// In modalità gruppo: peer di tutte le aziende; 1:1: solo aziende DIRECT
		const search$ = this.groupMode ? this.messagingService.searchGroupUsers(query, true) : this.messagingService.searchUsers(query, true);
		search$.subscribe({
			next: (data) => {
				this.users = data || [];
			},
			complete: () => {
				this.searchingUsers = false;
			},
		});
	}

	onUserFilter(event: any): void {
		this.searchUsers(event.filter || '');
	}

	createGroup(): void {
		const title = (this.groupName || '').trim();
		if (!title || this.groupSelectedUsers.length === 0) return;
		const ids = this.groupSelectedUsers.map((u) => u.usr_id);
		this.messagingService.createGroup(title, ids, true).subscribe({
			next: (conv) => {
				this.showNewConvDialog = false;
				this.messagingService.getConversations(true).subscribe((data) => {
					this.conversations = data || [];
					const found = this.conversations.find((c) => c.conv_id === conv.conv_id) || conv;
					this.selectConversation(found);
				});
			},
			error: () => this.configService.openErrorNotify('Errore creazione gruppo'),
		});
	}

	/** Modalità COMPANY, utente non membro: apre la chat di gruppo con l'azienda attiva. */
	startCompanyConversation(): void {
		this.messagingService.findOrCreateCompanyConversation(true).subscribe({
			next: (conv) => {
				this.showNewConvDialog = false;
				this.messagingService.getConversations(true).subscribe((data) => {
					this.conversations = data || [];
					const found = this.conversations.find((c) => c.conv_id === conv.conv_id) || conv;
					this.selectConversation(found);
				});
			},
			error: () => this.configService.openErrorNotify("Errore apertura chat con l'azienda"),
		});
	}

	startConversation(): void {
		if (!this.selectedUser) return;
		this.messagingService.findOrCreateConversation(this.selectedUser.usr_id, true).subscribe({
			next: (conv) => {
				this.showNewConvDialog = false;
				this.messagingService.getConversations(true).subscribe((data) => {
					this.conversations = data || [];
					const found = this.conversations.find((c) => c.conv_id === conv.conv_id) || conv;
					this.selectConversation(found);
				});
			},
			error: () => {
				this.configService.openErrorNotify('Errore creazione conversazione');
			},
		});
	}

	// --- Helpers conversazioni ---

	getOtherName(conv: CmpConversation): string {
		// Gruppi e aziende espongono other_company_name (titolo gruppo o nome azienda)
		if (conv.other_company_name) return conv.other_company_name;
		return [conv.other_firstname, conv.other_lastname].filter(Boolean).join(' ') || 'Utente';
	}

	getInitials(conv: CmpConversation): string {
		if (conv.other_company_name) return conv.other_company_name.substring(0, 2).toUpperCase();
		const f = (conv.other_firstname || '').charAt(0).toUpperCase();
		const l = (conv.other_lastname || '').charAt(0).toUpperCase();
		return f + l || '?';
	}

	getPreview(conv: CmpConversation): string {
		// null/undefined = nessun messaggio; stringa vuota = messaggio di solo allegato
		if (conv.last_message_text == null) return 'Nessun messaggio';
		const t = conv.last_message_text.trim();
		if (!t) return '📎 Allegato';
		return t.length > 38 ? t.substring(0, 38) + '…' : t;
	}

	// --- Azioni messaggio (modifica / elimina) ---

	isDeleted(msg: CmpMessage): boolean {
		return !!msg.msg_deleted_at;
	}

	isEdited(msg: CmpMessage): boolean {
		return !!msg.msg_edited_at;
	}

	openMsgMenu(event: Event, msg: CmpMessage, menu: any): void {
		const items: MenuItem[] = [];
		if (msg.msg_text) {
			items.push({ label: this.configService.getTranslate('Modifica'), icon: 'pi pi-pencil', command: () => this.openEditDialog(msg) });
		}
		items.push({ label: this.configService.getTranslate('Elimina'), icon: 'pi pi-trash', command: () => this.deleteMessage(msg) });
		this.msgMenuItems = items;
		menu.toggle(event);
	}

	openEditDialog(msg: CmpMessage): void {
		this.editingMsg = msg;
		this.editingText = msg.msg_text;
		this.showEditDialog = true;
	}

	saveEdit(): void {
		if (!this.editingMsg) return;
		const text = (this.editingText || '').trim();
		if (!text || text === this.editingMsg.msg_text) {
			this.showEditDialog = false;
			return;
		}
		const msg = this.editingMsg;
		this.messagingService.editMessage(msg.msg_id, text, true).subscribe({
			next: () => {
				msg.msg_text = text;
				msg.msg_edited_at = new Date().toISOString();
				this.showEditDialog = false;
			},
			error: () => this.configService.openErrorNotify('Errore modifica messaggio'),
		});
	}

	deleteMessage(msg: CmpMessage): void {
		this.messagingService.deleteMessage(msg.msg_id, true).subscribe({
			next: () => {
				msg.msg_deleted_at = new Date().toISOString();
				msg.msg_text = '';
				msg.attachments = [];
			},
			error: () => this.configService.openErrorNotify('Errore eliminazione messaggio'),
		});
	}

	// --- Allegati ---

	triggerFile(): void {
		this.fileInput?.nativeElement?.click();
	}

	onFilesPicked(event: Event): void {
		if (!this.selectedConv) return;
		const input = event.target as HTMLInputElement;
		if (!input.files || input.files.length === 0) return;
		const files = Array.from(input.files);
		const convId = this.selectedConv.conv_id;
		this.sending = true;
		this.messagingService.sendMedia(convId, this.newMessageText.trim(), files, true).subscribe({
			next: () => {
				this.newMessageText = '';
				this.loadMessages();
			},
			error: () => {
				this.sending = false;
				input.value = '';
				this.configService.openErrorNotify('Errore invio allegato');
			},
			complete: () => {
				this.sending = false;
				input.value = '';
			},
		});
	}

	private hydrateAttachments(): void {
		for (const msg of this.messages) {
			if (!msg.attachments) continue;
			for (const att of msg.attachments) {
				const cached = this.attUrlCache.get(att.att_id);
				if (cached) {
					att._objectUrl = cached;
					continue;
				}
				// Download gia' in corso (poll da 15s): non riaccodare la stessa richiesta
				if (this.attPending.has(att.att_id)) continue;
				this.attPending.add(att.att_id);
				this.messagingService.getAttachmentBlob(att.att_id).subscribe({
					next: (blob) => {
						this.attPending.delete(att.att_id);
						let url = this.attUrlCache.get(att.att_id);
						if (!url) {
							url = URL.createObjectURL(blob);
							this.attUrlCache.set(att.att_id, url);
						}
						att._objectUrl = url;
					},
					error: () => this.attPending.delete(att.att_id),
				});
			}
		}
	}

	isImage(att: CmpAttachment): boolean {
		return !!att.att_mime && att.att_mime.startsWith('image/');
	}

	openAttachment(att: CmpAttachment): void {
		if (att._objectUrl) window.open(att._objectUrl, '_blank');
	}

	getMsgSenderName(msg: CmpMessage): string {
		if (msg.sender_company_name) return msg.sender_company_name;
		return [msg.sender_firstname, msg.sender_lastname].filter(Boolean).join(' ') || 'Utente';
	}

	// --- Gestione gruppo ---

	openGroupDialog(): void {
		if (!this.selectedConv) return;
		this.groupAddSelected = null;
		this.groupAddUsers = [];
		this.loadGroupMembers();
		this.showGroupDialog = true;
	}

	private loadGroupMembers(): void {
		if (!this.selectedConv) return;
		this.messagingService.getGroupMembers(this.selectedConv.conv_id, true).subscribe({
			next: (members) => {
				this.groupMembers = members || [];
				const me = this.groupMembers.find((m) => m.cvm_usr_id === this.currentUserId);
				this.isGroupAdmin = me?.cvm_role === 'ADMIN';
			},
		});
	}

	memberName(m: CmpConversationMember): string {
		return [m.usr_firstname, m.usr_lastname].filter(Boolean).join(' ') || m.usr_username || 'Utente';
	}

	onGroupAddFilter(event: any): void {
		this.messagingService.searchGroupUsers(event.filter || '', true).subscribe({
			next: (data) => {
				const existing = new Set(this.groupMembers.map((m) => m.cvm_usr_id));
				this.groupAddUsers = (data || []).filter((u) => !existing.has(u.usr_id));
			},
		});
	}

	addGroupMember(): void {
		if (!this.selectedConv || !this.groupAddSelected) return;
		this.messagingService.addGroupMember(this.selectedConv.conv_id, this.groupAddSelected.usr_id, true).subscribe({
			next: () => {
				this.groupAddSelected = null;
				this.loadGroupMembers();
			},
			error: () => this.configService.openErrorNotify('Errore aggiunta membro'),
		});
	}

	removeGroupMember(m: CmpConversationMember): void {
		if (!this.selectedConv) return;
		this.messagingService.removeGroupMember(this.selectedConv.conv_id, m.cvm_usr_id, true).subscribe({
			next: () => this.loadGroupMembers(),
			error: () => this.configService.openErrorNotify('Errore rimozione membro'),
		});
	}

	renameGroup(): void {
		if (!this.selectedConv) return;
		const conv = this.selectedConv;
		const title = (conv.conv_title || conv.other_company_name || '').trim();
		// rinomina inline tramite prompt del browser (semplice e sufficiente lato web)
		const next = window.prompt('Nuovo nome del gruppo', title);
		if (next == null) return;
		const t = next.trim();
		if (!t) return;
		this.messagingService.renameGroup(conv.conv_id, t, true).subscribe({
			next: () => {
				conv.conv_title = t;
				conv.other_company_name = t;
			},
			error: () => this.configService.openErrorNotify('Errore rinomina gruppo'),
		});
	}

	leaveGroup(): void {
		if (!this.selectedConv) return;
		const convId = this.selectedConv.conv_id;
		this.messagingService.leaveGroup(convId, true).subscribe({
			next: () => {
				this.showGroupDialog = false;
				this.conversations = this.conversations.filter((c) => c.conv_id !== convId);
				this.selectedConv = null;
				this.messages = [];
			},
			error: () => this.configService.openErrorNotify('Errore'),
		});
	}

	// --- Helpers messaggi ---

	isOwnMessage(msg: CmpMessage): boolean {
		return msg.msg_sender_id === this.currentUserId;
	}

	getActiveInitials(): string {
		return this.selectedConv ? this.getInitials(this.selectedConv) : '?';
	}

	getMessageDate(msg: CmpMessage): string {
		if (!msg.msg_created_at) return '';
		return new Date(msg.msg_created_at).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' });
	}

	shouldShowDate(index: number): boolean {
		if (index === 0) return true;
		const curr = new Date(this.messages[index].msg_created_at).toDateString();
		const prev = new Date(this.messages[index - 1].msg_created_at).toDateString();
		return curr !== prev;
	}

	toggleSidebar(): void {
		this.sidebarOpen = !this.sidebarOpen;
	}

	private scrollToBottom(): void {
		if (this.messagesContainer) {
			const el = this.messagesContainer.nativeElement;
			el.scrollTop = el.scrollHeight;
		}
	}
}
